import { ScrapeHistory, ScrapeRequest } from '../types';
import { api } from '../utils/apiClient';

export enum ScrapeInterval {
  MANUAL = 'manual',
  EVERY_15_MINUTES = 'every_15_minutes',
  EVERY_30_MINUTES = 'every_30_minutes',
  EVERY_HOUR = 'every_hour',
  EVERY_2_HOURS = 'every_2_hours',
  EVERY_4_HOURS = 'every_4_hours',
  DAILY_TWICE = 'daily_twice',
  DAILY = 'daily',
  DAILY_5_TIMES = 'daily_5_times',
  EVERY_2_DAYS = 'every_2_days'
}

export enum SubredditStatus {
  ACTIVE = 'active',
  NOT_FOUND = 'not_found',
  BANNED = 'banned',
  PRIVATE = 'private',
  ERROR = 'error',
}

export interface MonitoredSubreddit {
  id: string;
  user_id: string;
  brand_id?: string;
  subreddit: string;
  keywords?: string[];
  added_at: string;
  last_scraped_at: string | null;
  scrape_count: number;
  reddit_url: string;
  auto_scrape_enabled: boolean;
  scrape_interval: string;
  next_scrape_at: string | null;
  post_count: number;
  status: SubredditStatus;
  status_message?: string | null;
  status_updated_at?: string | null;
}

export interface MonitoredSubredditCreate {
  subreddits: string[];
  keywords?: string[];
  brand_id?: string;
}

export interface SubredditSettingsUpdate {
  keywords?: string[];
  auto_scrape_enabled?: boolean;
  scrape_interval?: ScrapeInterval;
}

export interface SkippedSubreddit {
  subreddit: string;
  reason: string;
}

export interface AddMonitoredSubredditsResponse {
  monitored_subreddits: MonitoredSubreddit[];
  skipped_subreddits: SkippedSubreddit[];
}

export interface BulkScrapeResult {
  total_posts_found: number;
  subreddits_scraped: number;
  subreddits_skipped: number;
  results: Array<{
    subreddit: string;
    status: 'success' | 'skipped' | 'error';
    reason?: string;
    posts_found: number;
    new_posts_added?: number;
  }>;
}

export const monitoredSubredditsService = {
  async addMonitoredSubreddits(
    subreddits: string[], 
    keywords?: string[],
    brandId?: string
  ): Promise<AddMonitoredSubredditsResponse> {
    const payload: MonitoredSubredditCreate = { subreddits, keywords };
    if (brandId) {
      payload.brand_id = brandId;
    }
    return api.post<AddMonitoredSubredditsResponse>('/api/monitored-subreddits', payload);
  },
  
  async getMonitoredSubreddits(brandId?: string): Promise<MonitoredSubreddit[]> {
    let url = '/api/monitored-subreddits';
    if (brandId) {
      url += `?brand_id=${brandId}`;
    }
    return api.get<MonitoredSubreddit[]>(url);
  },

  async getMonitoredSubredditById(subredditId: string): Promise<MonitoredSubreddit> {
    return api.get<MonitoredSubreddit>(`/api/monitored-subreddits/${subredditId}`);
  },
  
  async deleteMonitoredSubreddit(subredditId: string): Promise<void> {
    await api.delete(`/api/monitored-subreddits/${subredditId}`);
  },
  
  async scrapeMonitoredSubreddit(
    subredditId: string,
    scrapeRequest?: ScrapeRequest
  ): Promise<ScrapeHistory> {
    return api.post<ScrapeHistory>(
      `/api/monitored-subreddits/${subredditId}/scrape`,
      scrapeRequest || { limit: 10 }
    );
  },
  
  async getScrapeHistory(subredditId: string, limit: number = 10): Promise<ScrapeHistory[]> {
    return api.get<ScrapeHistory[]>(
      `/api/monitored-subreddits/${subredditId}/scrape-history?limit=${limit}`
    );
  },
  
  async updateKeywords(subredditId: string, keywords: string[]): Promise<{ message: string; keywords: string[] }> {
    return api.put<{ message: string; keywords: string[] }>(
      `/api/monitored-subreddits/${subredditId}/keywords`,
      { keywords }
    );
  },
  
  async getMonitoredSubredditsCount(brandId?: string): Promise<{ count: number }> {
    let url = '/api/monitored-subreddits/count';
    if (brandId) {
      url += `?brand_id=${brandId}`;
    }
    return api.get<{ count: number }>(url);
  },

  async updateSettings(
    subredditId: string,
    settings: SubredditSettingsUpdate
  ): Promise<MonitoredSubreddit> {
    return api.put<MonitoredSubreddit>(
      `/api/monitored-subreddits/${subredditId}/settings`,
      settings
    );
  },

  async scrapeImmediately(subredditId: string, limit: number = 10): Promise<ScrapeHistory> {
    return api.post<ScrapeHistory>(
      `/api/monitored-subreddits/${subredditId}/scrape`,
      { limit }
    );
  },

  async scrapeAllSubreddits(brandId: string, limit: number = 10): Promise<BulkScrapeResult> {
    return api.post<BulkScrapeResult>(
      `/api/monitored-subreddits/scrape-all?brand_id=${brandId}&limit=${limit}`,
      {}
    );
  }
};


